<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import isEqual from 'lodash/isEqual'
import Pusher from 'pusher-js'
import type { RootData } from '~/definitions/AllDefinitions'
import { useMainStore } from '~/store/MainStore'
import { useProductStore } from '~/store/ProductStore'

const mainStore = useMainStore()
const productStore = useProductStore()
const { rootData } = storeToRefs(mainStore)
const structuredData = computed(() => mainStore.structuredData)
const { locale } = useI18n()
// Set locale during setup (not onMounted) so SSR renders in the correct language.
// Without this, SSR always renders English (i18n default), then client switches
// to the restaurant's language in onMounted — causing a hydration mismatch.
if (rootData.value.language) {
  locale.value = rootData.value.language.substring(0, 2)
}
const { refresh: refreshSeo } = useSiteSeo(rootData, structuredData)

function initPusher(id: number) {
  const pusher = new Pusher(import.meta.env.VITE_PUSHER_APP_KEY, {
    cluster: import.meta.env.VITE_PUSHER_CLUSTER,
  })
  const channel = pusher.subscribe(`bp-${id}`)
  channel.bind('root-update', (data: { domainName?: string }) => {
    if (data.domainName) {
      mainStore.getRootData(data.domainName)
    }
  }).bind('update-create-shop-category', (data?: { id?: number }) => {
    if (data && data.id) {
      productStore.updateCreateShopCategory(data.id)
    }
  }).bind('destroy-shop-category', (data?: { id?: number }) => {
    if (data && data.id) {
      productStore.destroyShopCategory(data.id)
    }
  }).bind('update-create-product', (data?: { id?: number, shopCategoryId?: number }) => {
    if (data && data.id && data.shopCategoryId) {
      productStore.updateCreateProduct(data.shopCategoryId, data.id)
    }
  }).bind('destroy-product', (data?: { id?: number, shopCategoryId?: number }) => {
    if (data && data.id && data.shopCategoryId) {
      productStore.destroyProduct(data.shopCategoryId, data.id)
    }
  })
}

function init(root: RootData) {
  if (root.id) {
    initPusher(root.id)
  }
  if (root.language) {
    locale.value = root.language.substring(0, 2)
  }
  refreshSeo()
}
onMounted(() => {
  init(rootData.value)
})
watch(() => rootData.value, async (newValue, oldValue) => {
  if (newValue && !isEqual(newValue, oldValue)) {
    await mainStore.getStructuredData()
    init(rootData.value)
  }
}, { deep: true })
</script>

<template>
  <NuxtLayout class="bg-tw-1">
    <NuxtPage class="bg-tw-1" />
  </NuxtLayout>
</template>
